/**
 * Badge Component
 *
 * ── 색 이름으로 쓰기 (Button 의 color 와 같은 이름) ──
 *   <Badge color="success">완료</Badge>               // → .badge--success
 *   <Badge color="error" size="md">오류</Badge>
 *   - color: primary | secondary | success | error | warning | info | gray
 *
 * ── 용도 이름으로 쓰기 (기존) ──
 *   <Badge variant="active">배포됨</Badge>            // size 미지정 → sm (기존 크기)
 *   <Badge variant="source-file" shape="tag">파일</Badge>
 *   - primary / gray: 강조 · 보조 구분 (상태가 아닌 종류 구분용, 예: 메뉴 단계 0 / 1)
 *   - source-file / source-web / source-database / source-drive: 데이터 소스 종류
 *   - role-admin / role-user: 역할 배지 (관리자 = 관리자 테마 primary, 사용자 = 사용자 테마 primary)
 *   - type: 종류 · 속성 표시용 태그 (흰 바탕 + 테두리, 살짝 둥근 네모)
 *       <Badge variant="type">MYSQL</Badge>   <Badge variant="type">1024차원</Badge>
 *
 * - color 를 주면 color 가 우선 (variant 무시)
 * - size: sm(기본, 기존 크기) | md | lg  → 스타일: index.css "Status Pills" 아래 Badge 크기
 * - shape: pill(기본, 알약) | tag(살짝 둥근 네모)  ※ type 은 shape 지정 없으면 tag 모양 (shape="pill" 로 알약 가능)
 * - dot: 글자 앞 동그라미 - 상태 표시용
 *       dot           → 글자색과 같은 색   <Badge color="gray" dot>동기화 완료</Badge>
 *       dot="success" → 점만 그 색        <Badge variant="type" shape="pill" dot="success">활성</Badge>
 * - 글자 앞에 아이콘(svg)을 같이 넣어도 됨 - 글자와 4px 간격, 글자 크기에 맞춰짐
 */

export type BadgeVariant =
  | 'active' | 'pending' | 'error' | 'primary' | 'gray' | 'role-admin' | 'role-user'
  | 'source-file' | 'source-web' | 'source-database' | 'source-drive' | 'type';

export type BadgeColor = 'primary' | 'secondary' | 'success' | 'error' | 'medium' | 'info' | 'gray';

interface BadgeProps {
  color?: BadgeColor;
  variant?: BadgeVariant;
  size?: 'sm' | 'md' | 'lg';
  shape?: 'pill' | 'tag';
  /** 글자 앞 동그라미 표시 (true = 글자색, 색 이름 = 그 색) */
  dot?: boolean | BadgeColor;
  children: React.ReactNode;
}

export function Badge({ color, variant = 'active', size = 'sm', shape, dot = false, children }: BadgeProps) {
  // 기본 모양(.status-pill) + 색 (.badge--{color}) 또는 용도 (variant)
  const className = color ? `status-pill badge--${color}` : `status-pill ${variant}`;
  // shape 미지정: type 은 tag, 그 외 pill
  const resolvedShape = shape ?? (!color && variant === 'type' ? 'tag' : 'pill');
  const shapeClass = resolvedShape === 'tag' ? ' badge--tag' : '';
  const dotClass = dot ? ` badge--dot${typeof dot === 'string' ? ` badge--dot-${dot}` : ''}` : '';

  return <span className={`badge ${className} badge--${size}${shapeClass}${dotClass}`}>{children}</span>;
}

export default Badge;
